import React, { useEffect, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';

const DEFAULT_START_DATE = 30;

/**
 * Displays a form used to search and filter the History list.
 * This component is also responsible for triggering history searches when the date range changes
 *  or when the form is submitted.
 *
 * @params userInfo The user details
 * @param daysBack How many days back to search, or 'ANY' to search all history
 * @param findOrders The function that does that actual search request
 * @param upateDays A function to update the main HistoryList component's Date filter
 */
export default function AdvancedPatientSearch({ userInfo, daysBack = DEFAULT_START_DATE, findOrders, updateDays }) {
	const isAdmin = userInfo.adminUser === 'true';

	const [showAdvancedForm, setShowAdvancedForm] = useState(false);
	const [canSubmit, setCanSubmit] = useState(false); // true will trigger a new search
	const [form, setForm] = useState({
		firstName: '',
		lastName: '',
		dateOfBirth: null,
		startDate: null,
		endDate: null,
		gsdlNo: '',
		activationId: '',
		gearOrderId: ''
	});

	const dobDatePickerRef = useRef(null);

	/**
	 * Update the start and end dates when daysBack changes.
	 * Also, set the canSubmit flag so a new search is executed any time daysBack changes.
	 */
	useEffect(() => {
		if (daysBack) {
			// SCR-7106: Only allow the "Any" days option for Admin users
			if (isAdmin && (daysBack === 'ANY' || +daysBack > 365)) {
				updateFormField('startDate', null);
				updateFormField('endDate', null);
			} else {
				let days = Number(daysBack);
				if (daysBack === 'ANY' || days > 365) {
					days = 365;
				} else if (isNaN(days)) {
					days = DEFAULT_START_DATE;
				}

				const startDate = new Date();
				startDate.setDate(startDate.getDate() - days);
				updateFormField('startDate', startDate);

				// Clear endDate unless days == 1 (yesterday), then set endDate = startDate
				let endDate = null;
				if (days === 1) {
					endDate = startDate;
				}
				updateFormField('endDate', endDate);
			}

			// Set the canSubmit flag so a new search is executed any time daysBack changes.
			setCanSubmit(true);
		}
	}, [daysBack, isAdmin]);

	/**
	 * Execute a new search any tme the form changes if the canSubmit flag is true.
	 */
	useEffect(() => {
		if (canSubmit) {
			setCanSubmit(false);
			findOrders(form);
		}
	}, [canSubmit, findOrders, form]);

	const toggleShowAdvancedForm = () => {
		setShowAdvancedForm((prevValue) => !prevValue);
	};

	const updateFormField = (fieldName, value) => {
		// Only allow numbers for gearOrderId
		if (fieldName === 'gearOrderId') {
			value = value?.replace(/\D/g, '');
		}

		setForm((prevForm) => {
			return {
				...prevForm,
				[fieldName]: value
			};
		});

		/* Clear out the date fields when non-date search criteria is entered.
		 * Searching by name or DOB should not be restricted to a date range unless the date filter is used.
		 */
		if (fieldName === 'firstName' || fieldName === 'lastName' || fieldName === 'dateOfBirth') {
			setForm((prevForm) => {
				return {
					...prevForm,
					startDate: null,
					endDate: null
				};
			});
		}
	};

	/**
	 * Validate the search form and trigger a new search request when the form is submitted.
	 */
	const handleSubmit = (event) => {
		event && event.preventDefault();

		// If no values are entered, reset to the default '30' day search
		if (!Object.values(form).some(Boolean)) {
			if (daysBack === `${DEFAULT_START_DATE}`) {
				// daysBack is already '30' so we can't rely on updateDays to trigger a new search. Force a 30 day search.
				const startDate = new Date();
				startDate.setDate(startDate.getDate() - DEFAULT_START_DATE);
				updateFormField('startDate', startDate);
				setCanSubmit(true);
			} else {
				// Mock the event object expected by the search request method
				const updateEvent = {
					preventDefault: () => {},
					currentTarget: {
						dataset: {
							days: `${DEFAULT_START_DATE}`
						}
					}
				};

				// Updating 'days' will pass in a new daysBack prop, which will be picked up in useEffect and trigger a new search
				updateDays(updateEvent);
			}
			return;
		}

		/*
		 * Update the parent's days value to clear out the date range (set to 'ANY')
		 *  and initate the search if we're not doing a date range search.
		 * This is done because we want to search for 'ANY' orders with matching criteria. Updating
		 *  the parent's days value will update the highlighting on the DATE search filter and will
		 *  also trigger the 'daysBack' useEffect, initating the search.
		 *
		 * If daysBack is already 'ANY', this approach won't trigger the useEffect. In that case, or
		 *  if we're doing a date range search, set the canSubmit value to trigger the search.
		 */
		let maxDays = isAdmin ? 'ANY' : '365';
		if (daysBack !== maxDays && form.startDate === null && form.endDate === null) {
			// Mock the event object expected by the search request method
			const updateEvent = {
				preventDefault: () => {},
				currentTarget: {
					dataset: {
						days: maxDays,
						skipURLUpdate: true // Do not update the searchString in the URL
					}
				}
			};

			// Updating 'days' will pass in a new daysBack prop, which will be picked up in useEffect and trigger a new search
			updateDays(updateEvent);
		} else {
			if (!isAdmin && form.startDate === null) {
				const startDate = new Date();
				startDate.setDate(startDate.getDate() - 365);
				updateFormField('startDate', startDate);
			}

			// Setting canSubmit will trigger the useEffect that does the search
			setCanSubmit(true);
		}
	};

	return (
		<>
			<form
				autoComplete="off"
				onSubmit={handleSubmit}
				style={{ margin: '0', paddingLeft: '10px' }}
			>
				<div className="row">
					<div className="col-sm-3">
						<div className="form-group">
							<label htmlFor="firstName" className="control-label">
								First Name
							</label>
							<input
								type="text"
								className="form-control"
								id="firstName"
								placeholder="First Name"
								value={form?.firstName}
								onChange={(e) => updateFormField('firstName', e.target.value)}
							/>
						</div>
					</div>
					<div className="col-sm-3">
						<div className="form-group">
							<label htmlFor="lastName" className="control-label">
								Last Name
							</label>
							<div>
								<input
									type="text"
									className="form-control"
									id="lastName"
									placeholder="Last Name"
									value={form?.lastName}
									onChange={(e) => updateFormField('lastName', e.target.value)}
								/>
							</div>
						</div>
					</div>
					<div className="col-sm-3">
						<div className="form-group fullWidthDatePicker">
							<label htmlFor="dob" className="control-label">
								Date of Birth
							</label>
							{/* <input type="text" className="form-control" id="dob" placeholder="MM/DD/YYYY" /> */}
							<ReactDatePicker
								className="form-control"
								selected={form.dateOfBirth}
								onChange={(date) => updateFormField('dateOfBirth', date)}
								maxDate={new Date()}
								showMonthDropdown
								showYearDropdown
								yearDropdownItemNumber={100}
								scrollableYearDropdown={true}
								placeholderText="mm/dd/yyyy"
								dateFormat={[
									'MM/dd/yyyy',
									'M/dd/yyyy',
									'M/d/yyyy',
									'MM/d/yyyy',
									'MM/dd/yy',
									'M/dd/yy',
									'M/d/yy',
									'MMddyyyy',
									'Mdyy',
									'Mdyyyy'
								]}
								// enableTabLoop={false}
								strictParsing
								ref={dobDatePickerRef}
								onFocus={() => dobDatePickerRef?.current?.input?.select()}
								onSubmit={() => {}}
								onKeyDown={(e) => {
									if (e.key === 'Tab') {
										dobDatePickerRef.current.setOpen(false);
									}
								}}
							/>
						</div>
					</div>
					{!showAdvancedForm && (
						<div className="col-sm-2">
							<div className="form-group">
								<label>&nbsp;</label>
								<br />
								<button type="submit" className="btn btn-default btn-patient-search radius" title="Search">
									Search <span className="ml-5 glyphicon glyphicon-search"></span>
								</button>
							</div>
						</div>
					)}
				</div>
				{!showAdvancedForm && (
					<div className="row">
						<div className="col-xs-12 pb-10">
							<button className="btn-link" onClick={toggleShowAdvancedForm}>
								<span className="glyphicon glyphicon-plus"></span> Advanced Search
							</button>
						</div>
					</div>
				)}
				{showAdvancedForm && (
					<AdvancedSearch onFieldUpdate={updateFormField} form={form} toggleDisplay={toggleShowAdvancedForm} admin={isAdmin} />
				)}
			</form>
		</>
	);
}

/**
 * Advanced search fields
 * 
 * @param form The search form values
 * @param onFieldUpdate A function to call when a field is changed
 * @param toggleDisplay A function that will hide/show this advanced search component
 * @param admin Whether the current user is an admin
 */
const AdvancedSearch = ({ form, onFieldUpdate, toggleDisplay, admin }) => {
	const startDatePickerRef = useRef(null);
	const endDatePickerRef = useRef(null);

	// Don't allow non-admins to search more than 365 days
	let minSearchDate = null;
	if (!admin) {
		minSearchDate = new Date();
		minSearchDate.setDate(minSearchDate.getDate() - 365);
	}

	return (
		<div className="panel panel-primary">
			<div className="panel-heading">
				Advanced Search
				<button type="button" className="close" onClick={toggleDisplay} title="Close">
					<span aria-hidden="true">&times;</span>
					<span className="sr-only">Close</span>
				</button>
			</div>
			<div className="panel-body">
				<p>Search by Activation, Requisition, or Confirmation ID to check the activation status of your order.</p>
				<div className="row">
					<div className="col-sm-4">
						<div className="form-group">
							<label htmlFor="activationId" className="control-label">
								Requisition or Activation #
							</label>
							<div>
								<input
									type="text"
									className="form-control"
									id="activationId"
									placeholder="Req or Activation #"
									value={form?.activationId}
									onChange={(e) => onFieldUpdate('activationId', e.target.value)}
								/>
							</div>
						</div>
					</div>
					<div className="col-sm-4">
						<div className="form-group">
							<label htmlFor="gearOrderId" className="control-label">
								Confirmation #
							</label>
							<div>
								<input
									type="text"
									className="form-control"
									id="gearOrderId"
									placeholder="Confirmation #"
									value={form?.gearOrderId}
									onChange={(e) => onFieldUpdate('gearOrderId', e.target.value)}
								/>
							</div>
						</div>
					</div>
					<div className="col-sm-2 col-sm-offset-1">
						<div className="form-group">
							<label>&nbsp;</label>
							<br />
							<button type="submit" className="btn btn-default btn-patient-search radius" title="Search">
								Search <span className="ml-5 glyphicon glyphicon-search"></span>
							</button>
						</div>
					</div>
				</div>
				<div className="row">
					{admin && (
						<div className="col-sm-4">
							<div className="form-group">
								<label htmlFor="gsdlNo" className="control-label">
									Practitioner
								</label>
								<div>
									<input
										type="text"
										className="form-control"
										id="gsdlNo"
										placeholder="GSDLNO"
										value={form?.gsdlNo}
										onChange={(e) => onFieldUpdate('gsdlNo', e.target.value)}
									/>
								</div>
							</div>
						</div>
					)}
					<div className="col-sm-4">
						<div className="form-group">
							<label className="control-label">Start Date</label>
							<div className="fullWidthDatePicker">
								<ReactDatePicker
									className="form-control"
									selected={form.startDate}
									onChange={(date) => onFieldUpdate('startDate', date)}
									selectsStart
									startDate={form.startDate}
									endDate={form.endDate}
									maxDate={new Date()}
									minDate={minSearchDate}
									showMonthDropdown
									showYearDropdown
									yearDropdownItemNumber={100}
									scrollableYearDropdown={true}
									placeholderText="mm/dd/yyyy"
									dateFormat={[
										'MM/dd/yyyy',
										'M/dd/yyyy',
										'M/d/yyyy',
										'MM/d/yyyy',
										'MM/dd/yy',
										'M/dd/yy',
										'M/d/yy',
										'MMddyyyy',
										'Mdyy',
										'Mdyyyy'
									]}
									strictParsing
									ref={startDatePickerRef}
									onFocus={() => startDatePickerRef?.current?.input?.select()}
									onKeyDown={(e) => {
										if (e.key === 'Tab') {
											startDatePickerRef.current.setOpen(false);
										}
									}}
								/>
							</div>
						</div>
					</div>
					<div className="col-sm-4">
						<div className="form-group">
							<label className="control-label">End Date</label>
							<div className="fullWidthDatePicker">
								<ReactDatePicker
									className="form-control"
									selected={form.endDate}
									onChange={(date) => onFieldUpdate('endDate', date)}
									selectsEnd
									startDate={form.startDate}
									endDate={form.endDate}
									minDate={form.startDate}
									maxDate={new Date()}
									showMonthDropdown
									showYearDropdown
									yearDropdownItemNumber={100}
									scrollableYearDropdown={true}
									placeholderText="mm/dd/yyyy"
									dateFormat={[
										'MM/dd/yyyy',
										'M/dd/yyyy',
										'M/d/yyyy',
										'MM/d/yyyy',
										'MM/dd/yy',
										'M/dd/yy',
										'M/d/yy',
										'MMddyyyy',
										'Mdyy',
										'Mdyyyy'
									]}
									strictParsing
									ref={endDatePickerRef}
									onFocus={() => endDatePickerRef?.current?.input?.select()}
									onKeyDown={(e) => {
										if (e.key === 'Tab') {
											endDatePickerRef.current.setOpen(false);
										}
									}}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};